import * as Sentry from '@sentry/react'
import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store'
import { doLogin } from '../../store/actions/authentication'
import { getRegistrationInfo } from '../../store/actions/registration'
import { useAPIData } from '../../store/axiosConfiguration'
import { useNavigate } from 'react-router-dom'
import { showToast } from '../../store/actions/layout'
import { AuthorizationHandler } from '../../store/authorizationHandler'
import { useTranslation } from 'react-i18next'
import SignRouterContainer from './SignRouterContainer'

type UserAuthorizationHandlerProps = {}

const UserAuthorizationHandler: React.FC<PropsWithChildren<UserAuthorizationHandlerProps>> = ({ children }) => {
  const { t } = useTranslation('general')
  const navigation = useNavigate()
  const dispatch = useAppDispatch()
  const authAPI = useAppSelector((state) => state.authentication)
  const registrationInfo = useAppSelector((state) => state.registration.info.data)
  const [authenticationError, setAuthenticationError] = useState<boolean>(false)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)

  useEffect(() => {
    if (registrationInfo?.userData) {
      Sentry.setUser({
        email: registrationInfo.userData.email,
        username: `${registrationInfo.userData.firstname} ${registrationInfo.userData.lastname}`.trim(),
      })
    }
  }, [registrationInfo])

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search)

    const params: { code?: string; error?: string } = Object.fromEntries(urlSearchParams.entries())

    if (params.code) {
      window.history.pushState({}, document.title, '/')

      dispatch(doLogin(params.code))
    } else if (params.error) {
      setAuthenticationError(true)
    } else {
      AuthorizationHandler.isSessionValid().then((result) => {
        if (!result) {
          window.location.replace(
            `${process.env.REACT_APP_COGNITO_URL}/login?client_id=${process.env.REACT_APP_CLIENT_ID}&response_type=code&scope=email+openid+profile&redirect_uri=${process.env.REACT_APP_BASE_URL}`,
          )
        } else {
          dispatch(getRegistrationInfo())
          setIsAuthenticated(true)
        }
      })
    }
  }, [dispatch, navigation])

  useAPIData(
    authAPI,
    useMemo(() => {
      return {
        onFulfilled: () => {
          dispatch(getRegistrationInfo())
          setIsAuthenticated(true)
        },
        onRejected() {
          dispatch(showToast({ summary: 'Login error', severity: 'error' }))
          setAuthenticationError(true)
        },
      }
    }, [dispatch]),
  )

  return isAuthenticated ? (
    <SignRouterContainer>{children}</SignRouterContainer>
  ) : (
    <>{authenticationError && <h1>{t('title.auth_error')}</h1>}</>
  )
}

export default UserAuthorizationHandler
